import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Link, Paper, Stack, Switch, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link as RouterLink } from 'react-router-dom';
import type { Artwork, Designer } from '@eprintsmart/shared';
import {
  createDesigner, moderateArtwork, setDesignerStatus,
  watchDesigners, watchOpenReports, watchSuspendedArtwork,
  type ArtworkReport,
} from '../services/marketplace';
import { SettlementDialog } from '../components/SettlementDialog';
import { inviteArtist } from '../services/notifications';
import { ApplicationQueue } from '../components/ApplicationQueue';

/**
 * Artists and the moderation queue.
 *
 * The queue comes FIRST on the page, above the artist list, because it is the
 * time-critical half: a report has already suspended the artwork and somebody
 * is waiting on an answer. India's IT Rules expect a complaint acknowledged
 * within 24 hours and resolved within 15 days, and a queue you have to scroll
 * to is a queue that gets missed.
 */

const AGE = (ms: number) => {
  const hours = Math.floor((Date.now() - ms) / 3_600_000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

export function Artists() {
  const [designers, setDesigners] = useState<Designer[]>([]);
  const [reports, setReports] = useState<ArtworkReport[]>([]);
  const [suspended, setSuspended] = useState<Artwork[]>([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: '', displayName: '', handle: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settling, setSettling] = useState<Designer | null>(null);
  const [invitingArtist, setInvitingArtist] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '' });
  const [inviteSent, setInviteSent] = useState<string | null>(null);
  const [resent, setResent] = useState<Set<string>>(new Set());

  const resendTo = async (email: string) => {
    setBusy(true);
    setError(null);
    try {
      await inviteArtist({ email });
      setResent((prev) => new Set(prev).add(email));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend that invite.');
    } finally {
      setBusy(false);
    }
  };

  const sendInvite = async () => {
    setBusy(true);
    setError(null);
    try {
      await inviteArtist({ email: inviteForm.email.trim() });
      setInviteSent(inviteForm.email.trim());
      setInvitingArtist(false);
      setInviteForm({ email: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send that invite.');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => watchDesigners(setDesigners), []);
  useEffect(() => watchOpenReports(setReports), []);
  useEffect(() => watchSuspendedArtwork(setSuspended), []);

  const byId = new Map(designers.map((d) => [d.id, d]));

  const act = async (artworkId: string, action: 'RESTORE' | 'REMOVE') => {
    setBusy(true);
    setError(null);
    try {
      await moderateArtwork({ artworkId, action });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      await createDesigner({
        email: form.email.trim(),
        displayName: form.displayName.trim(),
        handle: form.handle.trim().toLowerCase(),
      });
      setAdding(false);
      setForm({ email: '', displayName: '', handle: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add that artist.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {inviteSent && (
        <Alert severity="success" onClose={() => setInviteSent(null)}>
          Invited {inviteSent}. Nothing more to do — they sign in once with that address and
          their artist studio is already there. They choose their own display name and handle.
        </Alert>
      )}

      <ApplicationQueue />

      <Box>
        <Typography variant="h6" gutterBottom>
          Moderation queue{reports.length > 0 && ` (${reports.length})`}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          A report takes the artwork down straight away, before anyone reviews it — so this queue
          is work already done to the artist, not work waiting to be done. Answer the reporter
          within 24 hours.
        </Typography>

        <Paper variant="outlined">
          {reports.length === 0 && suspended.length === 0 ? (
            <Box p={2}><Typography variant="body2" color="text.secondary">Nothing reported.</Typography></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Artwork</TableCell>
                  <TableCell>Artist</TableCell>
                  <TableCell>Complaint</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell align="right">Decide</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.artworkId}</TableCell>
                    <TableCell>{byId.get(r.designerId)?.displayName ?? r.designerId}</TableCell>
                    <TableCell sx={{ maxWidth: 380 }}>
                      <Chip size="small" label={r.reason} sx={{ mr: 1 }} />
                      <Typography variant="body2" component="span">{r.detail}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.reporterName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.reporterEmail} · {AGE(r.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" disabled={busy}
                          onClick={() => void act(r.artworkId, 'RESTORE')}>
                          Put back
                        </Button>
                        <Button size="small" color="error" disabled={busy}
                          onClick={() => void act(r.artworkId, 'REMOVE')}>
                          Remove
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Suspended with no open report — withdrawn, or already
                    handled elsewhere. Shown so nothing sits down and forgotten. */}
                {suspended
                  .filter((a) => !reports.some((r) => r.artworkId === a.id))
                  .map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{a.title}</TableCell>
                      <TableCell>{byId.get(a.designerId)?.displayName ?? a.designerId}</TableCell>
                      <TableCell colSpan={2}>
                        <Typography variant="body2" color="text.secondary">
                          Suspended, no open report — {a.moderation?.reason ?? 'no reason recorded'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" disabled={busy}
                            onClick={() => void act(a.id, 'RESTORE')}>Put back</Button>
                          <Button size="small" color="error" disabled={busy}
                            onClick={() => void act(a.id, 'REMOVE')}>Remove</Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>

      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h6">Artists ({designers.length})</Typography>
          <Stack direction="row" spacing={1}>
            {/* INVITE IS THE PRIMARY ACTION, and it was not until an invite
                could claim itself. While createDesigner was the only door,
                "Add artist" had to be the prominent one and the invite was a
                preliminary — which is exactly how somebody ends up typing a
                brand-new address into Add and meeting a rejection that reads
                like a bug. Invite now works for anybody; Add only works for
                somebody who already has an account, so it is the edge case and
                looks like one. */}
            <Button startIcon={<AddIcon />} variant="contained" size="small"
              onClick={() => setInvitingArtist(true)}>
              Invite an artist
            </Button>
            <Button size="small" onClick={() => setAdding(true)}>
              Add an existing account
            </Button>
          </Stack>
        </Stack>

        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Handle</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>PAN</TableCell>
                <TableCell>Featured</TableCell>
                <TableCell align="right">Status</TableCell>
                <TableCell align="right">Invite</TableCell>
                <TableCell align="right">Pay</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {designers.map((d) => (
                <TableRow key={d.id} hover>
                  <TableCell>
                    {/* The name is the link, not the whole row: the row carries
                        switches and buttons, and a row-level click would fire
                        every time somebody meant to toggle Featured. */}
                    <Link component={RouterLink} to={`/artists/${d.id}`} underline="hover">
                      {d.displayName}
                    </Link>
                  </TableCell>
                  <TableCell>/artist/{d.handle}</TableCell>
                  <TableCell>{d.email}</TableCell>
                  <TableCell>
                    {/* Flagged because it decides the TDS rate: 10% with a PAN,
                        20% without. A missing one costs the artist money. */}
                    {d.pan
                      ? <Chip size="small" label="on file" color="success" variant="outlined" />
                      : <Chip size="small" label="missing — TDS 20%" color="warning" />}
                  </TableCell>
                  <TableCell>
                    <Switch size="small" checked={Boolean(d.featured)} disabled={busy}
                      onChange={(e) => void setDesignerStatus({ designerId: d.id, featured: e.target.checked })} />
                  </TableCell>
                  <TableCell align="right">
                    <FormControlLabel
                      control={
                        <Switch size="small" checked={d.status === 'ACTIVE'} disabled={busy}
                          onChange={(e) => void setDesignerStatus({
                            designerId: d.id, status: e.target.checked ? 'ACTIVE' : 'SUSPENDED',
                          })} />
                      }
                      label={<Typography variant="caption">{d.status.toLowerCase()}</Typography>}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {/* Resendable because people lose emails. inviteArtist
                        counts previous invites and increments the queue id, so
                        a genuine resend is not swallowed as a duplicate. */}
                    <Button size="small" disabled={busy}
                      onClick={() => void resendTo(d.email)}>
                      {resent.has(d.email) ? 'Sent' : 'Resend'}
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => setSettling(d)}>Statement</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      {settling && <SettlementDialog designer={settling} onClose={() => setSettling(null)} />}

      <Dialog open={invitingArtist} onClose={() => setInvitingArtist(false)} fullWidth maxWidth="sm">
        <DialogTitle>Invite an artist</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            They sign in once with this address and their artist studio is already there —
            nothing else to do here afterwards. They choose their own display name and handle
            in the studio, because the handle becomes their public page address and can only
            be set once.
          </Alert>
          <Stack spacing={2} mt={1}>
            <TextField label="Email" value={inviteForm.email} fullWidth autoFocus
              helperText="The address they will sign in with. That is all we need."
              onChange={(e) => setInviteForm({ email: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvitingArtist(false)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !inviteForm.email}
            onClick={() => void sendInvite()}>Send invite</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={adding} onClose={() => setAdding(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add an artist</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Only for somebody who already has an account here.</strong> For anybody else,
            close this and use <strong>Invite an artist</strong> — they sign in once and their
            studio is waiting, with no second step from you.
          </Alert>
          <Stack spacing={2} mt={1}>
            <TextField label="Email" value={form.email} fullWidth
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <TextField label="Display name" value={form.displayName} fullWidth
              onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
            <TextField label="Handle" value={form.handle} fullWidth
              helperText="Their page address. Fixed once set, so links they share keep working."
              onChange={(e) => setForm({ ...form, handle: e.target.value.toLowerCase() })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(false)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !form.email || !form.displayName || !form.handle}
            onClick={() => void add()}>Add</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
